import { useState, useEffect, useRef } from 'react';
import WMIcon from './WMIcon';

function fmtDuration(ms) {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * TimeTracker — per-step timer widget for work orders.
 *
 * Props:
 *   workOrderId   — ID of the work order
 *   stepId        — ID of the step being timed (null = general WO timer)
 *   techName      — name of the tech starting the timer
 *   timeEntries   — existing time entries for this step
 *   onStart(entry)  — called when timer starts { workOrderId, stepId, techName, startedAt }
 *   onStop(entry)   — called when timer stops { ...entry, stoppedAt, minutes }
 */
export default function TimeTracker({ workOrderId, stepId = null, techName = 'Me', timeEntries = [], onStart, onStop }) {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const intervalRef = useRef(null);

  // Compute total from completed entries for this step
  const totalMinutes = timeEntries
    .filter(e => e.stepId === stepId && e.stoppedAt)
    .reduce((sum, e) => sum + (e.minutes || 0), 0);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setElapsed(Date.now() - new Date(startedAt).getTime());
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
      setElapsed(0);
    }
    return () => clearInterval(intervalRef.current);
  }, [running, startedAt]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleStart = () => {
    const now = new Date().toISOString();
    setStartedAt(now);
    setRunning(true);
    onStart?.({ workOrderId, stepId, techName, startedAt: now });
  };

  const handleStop = () => {
    const stoppedAt = new Date().toISOString();
    const minutes = Math.round(elapsed / 60000);
    setRunning(false);
    onStop?.({ workOrderId, stepId, techName, startedAt, stoppedAt, minutes });
  };

  return (
    <div className="inline-flex items-center gap-2">
      {/* Elapsed display */}
      {running && (
        <span className="text-[var(--wm-text-ui)] font-mono text-blue-600 dark:text-blue-400 tabular-nums min-w-[40px]">
          {fmtDuration(elapsed)}
        </span>
      )}
      {/* Total logged */}
      {totalMinutes > 0 && !running && (
        <span className="text-[var(--wm-text-ui)] text-gray-500 dark:text-gray-400">
          {totalMinutes >= 60
            ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`
            : `${totalMinutes}m`} logged
        </span>
      )}
      {/* Start/Stop button */}
      <button
        onClick={running ? handleStop : handleStart}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[var(--wm-text-ui)] font-medium transition-colors
          ${running
            ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
          }`}
        title={running ? 'Stop timer' : 'Start timer'}
      >
        <WMIcon name={running ? 'pause' : 'play'} className="w-3 h-3" />
        {running ? 'Stop' : 'Start'}
      </button>
    </div>
  );
}
